import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, Mail, RefreshCw, Zap, CheckCircle2, AlertTriangle, Search, X, Check } from "lucide-react";
import { toast } from "sonner";

// resendMode = true when the invoice is already synced (only re-emailing is allowed).
export default function SendInvoiceDialog({ inv, open, onOpenChange, onDone, resendMode = false }) {
  const [ccEmails, setCcEmails] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [ccQuery, setCcQuery] = useState("");
  const [approved] = useState(true);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !inv) return;
    setCcEmails([]); setCcQuery(""); setValidation(null); setContacts([]);
    api.get(`/invoices/${inv.id}/validate`).then((r) => setValidation(r.data)).catch(() => setValidation({ valid: false, missing: ["Could not validate invoice"] }));
    if (inv.customer_id) {
      api.get(`/customers/${inv.customer_id}`).then((r) => setContacts((r.data.contacts || []).filter((c) => c.email))).catch(() => setContacts([]));
    }
  }, [open, inv]);

  if (!inv) return null;
  const valid = validation?.valid;
  const gatedDisabled = !valid || !!busy;
  const toggleCc = (email) => setCcEmails((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  const ccMatches = ccQuery
    ? contacts.filter((c) => `${c.name} ${c.position} ${c.email}`.toLowerCase().includes(ccQuery.toLowerCase()))
    : contacts;

  const run = async (action) => {
    setBusy(action);
    const ccArr = ccEmails;
    try {
      let res;
      if (action === "email_pdf" || action === "resend") res = await api.post(`/invoices/${inv.id}/email`, { approved, cc: ccArr, mark_sent: false });
      else if (action === "send_invoice") res = await api.post(`/invoices/${inv.id}/email`, { approved, cc: ccArr, mark_sent: true });
      else if (action === "sync") res = await api.post(`/invoices/${inv.id}/sync-xero`, { approved });
      else if (action === "email_sync") res = await api.post(`/invoices/${inv.id}/email-and-sync`, { approved, cc: ccArr });
      else if (action === "mark_sent") res = await api.post(`/invoices/${inv.id}/mark-sent`);
      toast.success(res.data?.message || "Done");
      onOpenChange(false); onDone(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-invoice-dialog">
        <DialogHeader><DialogTitle className="font-heading">{resendMode ? "Resend" : "Send / Sync"} Invoice {inv.invoice_number}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-400 -mt-2">Emails the latest Invoice PDF via Resend and/or syncs to Xero. All recipients are logged in the audit history.</DialogDescription>

        <div className="space-y-3">
          {/* Validation panel */}
          {validation && (validation.valid ? (
            <div data-testid="send-validation-ok" className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">
              <CheckCircle2 className="w-4 h-4" />All mandatory invoice information is complete.
            </div>
          ) : (
            <div data-testid="send-validation-missing" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
              <p className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" />Complete these before sending or syncing:</p>
              <ul className="list-disc ml-6 mt-1 text-xs">
                {(validation.missing || []).map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          ))}

          <div className="text-sm">
            <Label>Send to (Billing Email)</Label>
            <div data-testid="send-to" className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-navy">{inv.billing_email || <span className="text-red-500">No billing email set</span>}</div>
          </div>
          {inv.accounts_email && inv.accounts_email !== inv.billing_email && (
            <p className="text-xs text-slate-500" data-testid="send-autocc">Auto-CC: {inv.accounts_email} (Accounts Email)</p>
          )}
          <div>
            <Label>CC — Additional Contacts</Label>
            {ccEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="cc-selected">
                {ccEmails.map((email) => {
                  const c = contacts.find((x) => x.email === email);
                  return (
                    <span key={email} className="inline-flex items-center gap-1 bg-navy/5 border border-navy/10 text-navy rounded-full pl-2.5 pr-1.5 py-1 text-xs">
                      {c ? `${c.name}` : email}
                      <button type="button" data-testid={`cc-remove-${email}`} onClick={() => toggleCc(email)} className="hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative mt-1.5">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="cc-search" value={ccQuery} onChange={(e) => setCcQuery(e.target.value)} placeholder="Search contacts to CC…" className="pl-9" />
            </div>
            <div className="mt-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {ccMatches.length === 0 ? (
                <p className="text-xs text-slate-400 px-3 py-2">{contacts.length === 0 ? "No additional contacts with an email for this customer." : "No matching contacts."}</p>
              ) : ccMatches.map((c) => {
                const sel = ccEmails.includes(c.email);
                return (
                  <button key={c.email + c.name} type="button" data-testid={`cc-option-${c.email}`} onClick={() => toggleCc(c.email)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${sel ? "bg-navy/5" : "hover:bg-slate-50"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel ? "bg-navy border-navy" : "border-slate-300"}`}>
                      {sel && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-navy">{c.name}</span>
                      {c.position && <span className="text-slate-400"> · {c.position}</span>}
                      <span className="block text-xs text-slate-400 truncate">{c.email}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          {resendMode ? (
            <Button data-testid="action-resend" onClick={() => run("resend")} disabled={!!busy} className="w-full bg-navy hover:bg-navy-light">
              <Mail className="w-4 h-4 mr-2" />{busy === "resend" ? "Resending…" : "Resend Invoice"}
            </Button>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              <Button data-testid="action-email-sync" onClick={() => run("email_sync")} disabled={gatedDisabled} className="bg-navy hover:bg-navy-light sm:col-span-2">
                <Zap className="w-4 h-4 mr-2" />{busy === "email_sync" ? "Processing…" : "Email & Sync to Xero"}
              </Button>
              <Button data-testid="action-send-invoice" variant="outline" onClick={() => run("send_invoice")} disabled={gatedDisabled}>
                <Send className="w-4 h-4 mr-2" />Send Invoice
              </Button>
              <Button data-testid="action-email-pdf" variant="outline" onClick={() => run("email_pdf")} disabled={gatedDisabled}>
                <Mail className="w-4 h-4 mr-2" />Email PDF
              </Button>
              <Button data-testid="action-sync" variant="outline" onClick={() => run("sync")} disabled={gatedDisabled}>
                <RefreshCw className="w-4 h-4 mr-2" />Sync to Xero
              </Button>
              <Button data-testid="action-mark-sent" variant="outline" onClick={() => run("mark_sent")} disabled={!!busy}>
                Mark as Sent
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
